import { Menu } from 'lucide-react';
import * as React from 'react';

import { GlobalBanner } from '@/global/announcement/components/global-banner';
import { UserNavigation } from '@/global/navigation/components/UserNavigation';
import MainSearch from '@/global/search/components/MainSearch';

import { Accordion } from '../ui/accordion';
import { Button } from '../ui/button';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '../ui/navigation-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';

import { Logo } from './logo';
import { Progress } from './progress';

type MainLayoutProps = {
  children: React.ReactNode;
};

const menu = [
  { title: 'Server', url: '/server' },
  {
    title: '2D Viewer',
    url: '/demo-viewer',
  },
  {
    title: 'Team Finder',
    url: '/team-finder',
  },
];

export const MainLayout = ({ children }: MainLayoutProps) => {
  return (
    <div className="flex h-dvh overflow-hidden">
      <div className="flex w-0 flex-1 flex-col overflow-hidden ">
        <GlobalBanner />
        <header className="relative z-10 flex h-16 shrink-0 border-b border-border/40 bg-background/95 shadow-sm backdrop-blur-sm supports-backdrop-filter:bg-background/60">
          <Progress />

          {/* Desktop Menu */}
          <div className="flex flex-1 items-center px-4 md:px-6 lg:px-8">
            <nav className="hidden justify-between lg:flex">
              <div className="flex items-center gap-6">
                <Logo className="w-16 sm:w-20" />
                <div className="flex items-center">
                  <NavigationMenu>
                    <NavigationMenuList>
                      {menu.map((item) => (
                        <NavigationMenuItem key={item.title}>
                          <NavigationMenuLink
                            href={item.url}
                            className="group inline-flex h-10 w-max items-center justify-center rounded-md !bg-background px-4 py-2 text-sm tracking-wide text-foreground/80 transition-colors hover:text-foreground"
                          >
                            {item.title}
                          </NavigationMenuLink>
                        </NavigationMenuItem>
                      ))}
                    </NavigationMenuList>
                  </NavigationMenu>
                </div>
              </div>
            </nav>

            {/* Mobile Menu */}
            <div className="block lg:hidden">
              <div className="flex items-center justify-between">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="size-9">
                      <Menu className="size-4" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent className="overflow-y-auto" side="left">
                    <SheetHeader>
                      <SheetTitle>
                        <Logo className="w-20" />
                      </SheetTitle>
                    </SheetHeader>
                    <div className="flex flex-col gap-6 pt-6">
                      <Accordion
                        type="single"
                        collapsible
                        className="flex w-full flex-col gap-4"
                      >
                        {menu.map((item) => (
                          <a
                            key={item.title}
                            href={item.url}
                            className="text-md font-semibold"
                          >
                            {item.title}
                          </a>
                        ))}
                      </Accordion>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
            <div className="ml-auto w-full px-4 sm:w-auto">
              <MainSearch />
            </div>
            <div className="flex items-center">
              <UserNavigation />
            </div>
          </div>
        </header>
        <main className="relative flex-1 overflow-y-auto focus:outline-hidden">
          {children}
        </main>
      </div>
    </div>
  );
};
